// src/pages/journalStock/filterJournalStock/FilterJournalStock.jsx

import React, { useState, useEffect } from "react";
import {
    Row,
    Col,
    Select,
    DatePicker,
    Button,
    Card,
    message,
} from "antd";
import {
    SearchOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import dayjs from 'dayjs';
import useJournalStockData from "../hooks/useJournalStockData";

const { RangePicker } = DatePicker;

const FilterJournalStock = ({ 
    onSearch, 
    initialFilters = {}, 
    loading: parentLoading = false 
}) => {
    const { 
        marque = [], 
        pointure = [], 
        loading: hookLoading = false 
    } = useJournalStockData();

    const getInitialDateRange = () => {
        if (initialFilters.date_debut && initialFilters.date_fin) {
            return [dayjs(initialFilters.date_debut), dayjs(initialFilters.date_fin)];
        }
        return null;
    };

    const [filters, setFilters] = useState({
        marque_id: initialFilters.marque_id || undefined,    // ✅ V4
        taille_id: initialFilters.taille_id || undefined,    // ✅ V4
        dateRange: getInitialDateRange(),
    });

    useEffect(() => {
        const hasParentFilters = initialFilters && Object.keys(initialFilters).some(
            key => initialFilters[key] !== null && initialFilters[key] !== undefined
        );

        if (hasParentFilters) {
            const newDateRange = (initialFilters.date_debut && initialFilters.date_fin)
                ? [dayjs(initialFilters.date_debut), dayjs(initialFilters.date_fin)]
                : null;

            setFilters(prev => ({
                ...prev,
                marque_id: initialFilters.marque_id !== undefined ? initialFilters.marque_id : prev.marque_id,
                taille_id: initialFilters.taille_id !== undefined ? initialFilters.taille_id : prev.taille_id,
                dateRange: newDateRange !== null ? newDateRange : prev.dateRange,
            }));
        }
    }, [initialFilters]);

    const handleSearch = () => {
        if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
            if (filters.dateRange[0].isAfter(filters.dateRange[1])) {
                message.error("La date de début doit être antérieure à la date de fin");
                return;
            }
        }

        // ✅ V4 : Normaliser les valeurs
        const normalizeValue = (value) => {
            if (!value) return null;
            if (Array.isArray(value)) {
                return value.length > 0 ? value.join(',') : null;
            }
            return value;
        };

        const payload = {
            marque_id: normalizeValue(filters.marque_id),    // ✅ V4
            taille_id: normalizeValue(filters.taille_id),    // ✅ V4
            date_debut: filters.dateRange?.[0]?.format("YYYY-MM-DD") || null,
            date_fin: filters.dateRange?.[1]?.format("YYYY-MM-DD") || null,
        };

        if (onSearch) {
            onSearch(payload);
        }
    };

    const handleReset = () => {
        setFilters({
            marque_id: undefined,
            taille_id: undefined,
            dateRange: null,
        });

        if (onSearch) {
            onSearch({
                marque_id: null,
                taille_id: null,
                date_debut: null,
                date_fin: null,
            });
        }
    };

    const isLoading = hookLoading || parentLoading;

    return (
        <Card bordered={false} style={{ marginBottom: 20, borderRadius: 12 }}>
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                    <Select
                        style={{ width: "100%" }}
                        placeholder="Toutes les marques"
                        allowClear
                        loading={isLoading}
                        mode="multiple"
                        maxTagCount="responsive"
                        value={filters.marque_id}
                        onChange={(value) =>
                            setFilters((prev) => ({
                                ...prev,
                                marque_id: value,
                            }))
                        }
                        options={Array.isArray(marque) ? marque.map((item) => ({
                            value: item.id_marque,
                            label: item.nom,
                        })) : []}
                    />
                </Col>

                <Col xs={24} sm={12} md={4}>
                    <Select
                        style={{ width: "100%" }}
                        placeholder="Toutes les pointures"
                        allowClear
                        showSearch
                        loading={isLoading}
                        mode="multiple"
                        maxTagCount="responsive"
                        value={filters.taille_id}
                        optionFilterProp="label"
                        onChange={(value) =>
                            setFilters((prev) => ({
                                ...prev,
                                taille_id: value,
                            }))
                        }
                        options={Array.isArray(pointure) ? pointure.map((item) => ({
                            value: item.id_taille,
                            label: item.taille,
                        })) : []}
                    />
                </Col>

                <Col xs={24} sm={24} md={6}>
                    <RangePicker
                        style={{ width: "100%" }}
                        format="DD/MM/YYYY"
                        value={filters.dateRange}
                        onChange={(dates) =>
                            setFilters((prev) => ({
                                ...prev,
                                dateRange: dates,
                            }))
                        }
                        placeholder={["Date début", "Date fin"]}
                    />
                </Col>

                <Col xs={24} md={4}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            loading={isLoading}
                        >
                            Filtrer
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={handleReset}
                            loading={isLoading}
                        >
                            Reset
                        </Button>
                    </div>
                </Col>
            </Row>
        </Card>
    );
};

export default FilterJournalStock;